/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// เพิ่ม/แก้ เหตุการณ์แจ้งเตือนที่ร้านตั้งเอง (migration 0031 · 25 ต.ค. 2569)
// ชื่อ · คำอธิบาย · ข้อความ (ตัวแปรวันเวลา · *ตัวหนา*) · ตั้งเวลาส่งอัตโนมัติ (เวลา + วันในสัปดาห์)
// บันทึก → OPTIONS /notify_setting/save_event · เหตุการณ์ขึ้นในตารางให้ติ๊กกลุ่มเหมือนเหตุการณ์ของระบบ
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { FetchData } from 'services/Request';

const DAY_FULL = ['', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์', 'อาทิตย์'];
/* วันส่งครั้งแรก (1 = จันทร์ … 7 = อาทิตย์) นับจากวันนี้ · วันนี้ใช้ไม่ได้ถ้าเวลาผ่านไปแล้ว · วันนี้ = ไม่ต้องบอก */
const firstSendText = (days, passedToday, time) => {
  const now = new Date();
  const today = ((now.getDay() + 6) % 7) + 1;
  const pick = days.length ? days.map(Number) : [1, 2, 3, 4, 5, 6, 7];
  for (let off = 0; off < 8; off += 1) {
    const d = ((today - 1 + off) % 7) + 1;
    if (pick.includes(d) && !(off === 0 && passedToday)) {
      if (off === 0) return '';
      return ` · ส่งอัตโนมัติครั้งแรก${off === 1 ? 'พรุ่งนี้' : `วัน${DAY_FULL[d]}`} ${time} น.`;
    }
  }
  return '';
};

const DAYS = [
  [1, 'จ'],
  [2, 'อ'],
  [3, 'พ'],
  [4, 'พฤ'],
  [5, 'ศ'],
  [6, 'ส'],
  [7, 'อา'],
];
const VARS = { วันที่: 'วันที่ส่ง', เวลา: 'เวลาที่ส่ง', ชื่อเหตุการณ์: 'ชื่อเหตุการณ์' };
const ERR = {
  event_name_required: 'ตั้งชื่อเหตุการณ์ก่อน (ไม่เกิน 100 ตัวอักษร)',
  message_required: 'พิมพ์ข้อความก่อน',
  template_unknown_var: 'ใช้ได้เฉพาะตัวแปรในชิป',
  template_too_long: 'ข้อความยาวเกิน 2,000 ตัวอักษร',
  schedule_time_invalid: 'ใส่เวลาส่งให้ถูกต้อง เช่น 08:00',
  custom_table_missing: 'เซิร์ฟเวอร์ยังไม่รองรับ (ยังไม่รันสคริปต์ฐานข้อมูล 0031) — แจ้งไอที',
  template_table_missing: 'เซิร์ฟเวอร์ยังไม่รองรับ (ยังไม่รันสคริปต์ฐานข้อมูล 0029) — แจ้งไอที',
  event_not_found: 'ไม่พบเหตุการณ์นี้แล้ว โหลดหน้าใหม่',
  permission_denied: 'บัญชีนี้ไม่มีสิทธิ์ตั้งค่าแจ้งเตือน',
};

export default function NotifyEventDialog({
  open,
  onClose,
  event,
  initialMessage,
  token,
  mod,
  onSaved,
  onNotify,
}) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [text, setText] = useState('');
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [schedOn, setSchedOn] = useState(false);
  const [time, setTime] = useState('08:00');
  const [days, setDays] = useState([1, 2, 3, 4, 5, 6, 7]);
  const [busy, setBusy] = useState(false);
  const areaRef = useRef(null);
  const caret = useRef(null); // ตำแหน่งเคอร์เซอร์หลังแทรกตัวแปร — ตั้งหลัง React เขียนค่าใหม่ลงช่องแล้ว
  const timer = useRef(null);
  const seq = useRef(0); // คำขอพรีวิวล่าสุด — คำตอบเก่าที่มาทีหลังถูกทิ้ง
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
  }, []);

  useLayoutEffect(() => {
    /* ก่อนวาดเฟรมถัดไป — พิมพ์ต่อได้ทันทีหลังกดชิป ไม่ต้องรอ effect ปกติ */
    const el = areaRef.current;
    if (caret.current === null || !el) return;
    const pos = caret.current;
    caret.current = null;
    el.focus();
    el.setSelectionRange(pos, pos);
  }, [text]);

  const askPreview = (value) => {
    clearTimeout(timer.current);
    if (!value.trim()) {
      setPreview('');
      setError('');
      return;
    }
    timer.current = setTimeout(() => {
      seq.current += 1;
      const mine = seq.current;
      FetchData('OPTIONS', `/${mod}/preview_template`, token, {
        code: event && event.code ? event.code : 'custom',
        template: value,
        _t: Date.now(),
      })
        .then((r) => {
          if (!alive.current || mine !== seq.current || r.status !== 'ok') return;
          setPreview(r.preview || '');
          setError(r.error ? ERR[r.error] || r.error : '');
        })
        .catch(() => {});
    }, 400);
  };

  useEffect(() => {
    if (!open) return;
    setName(event ? event.name || '' : '');
    setDesc(event ? event.description || '' : '');
    setText(initialMessage || '');
    setPreview('');
    setError('');
    const t = event && event.schedule_time ? String(event.schedule_time).slice(0, 5) : '';
    setSchedOn(Boolean(t));
    setTime(t || '08:00');
    const d =
      event && event.schedule_days ? String(event.schedule_days).split(',').map(Number) : [];
    setDays(d.length ? d : [1, 2, 3, 4, 5, 6, 7]);
    if (initialMessage) askPreview(initialMessage);
  }, [open]);

  const change = (v) => {
    setText(v);
    askPreview(v);
  };
  const insertVar = (v) => {
    const el = areaRef.current;
    const tag = `{${v}}`;
    if (!el || typeof el.selectionStart !== 'number') {
      change(`${text}${tag}`);
      return;
    }
    const s = el.selectionStart;
    const e = el.selectionEnd;
    caret.current = s + tag.length;
    change(text.slice(0, s) + tag + text.slice(e));
  };
  const toggleDay = (d) =>
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()));

  const save = () => {
    if (busy) return;
    setBusy(true);
    FetchData('OPTIONS', `/${mod}/save_event`, token, {
      code: event && event.code ? event.code : '',
      name,
      description: desc,
      message: text,
      schedule_on: schedOn ? 'Y' : 'N',
      schedule_time: time,
      schedule_days: days,
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current) return;
        setBusy(false);
        if (r.status === 'ok') {
          /* บอกวันที่จะส่งอัตโนมัติครั้งแรกเมื่อไม่ใช่วันนี้ — เวลาผ่านไปแล้ว หรือวันนี้ไม่ได้เลือกไว้ (รีเช็ค 26 ต.ค.) */
          const next = schedOn ? firstSendText(days, r.starts_next_day === 'Y', time) : '';
          onNotify(
            event
              ? `แก้เหตุการณ์แล้ว${next}`
              : `เพิ่มเหตุการณ์แล้ว${next} — ติ๊กกลุ่มที่จะรับในตาราง แล้วกด "ส่งตอนนี้" ได้เลย`,
            'success'
          );
          onSaved();
          onClose();
        } else onNotify(ERR[r.msg] || 'บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
      })
      .catch(() => {
        if (alive.current) {
          setBusy(false);
          onNotify('บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
        }
      });
  };

  const canSave =
    name.trim() &&
    text.trim() &&
    !error &&
    (!schedOn || (/^\d{2}:\d{2}$/.test(time) && days.length));

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth='md'>
      <DialogTitle>{event ? 'แก้เหตุการณ์ของร้าน' : 'เพิ่มเหตุการณ์ของร้าน'}</DialogTitle>
      <DialogContent dividers>
        <MDBox display='flex' gap={2} flexWrap='wrap'>
          <MDBox flex='1 1 360px' minWidth={0} display='flex' flexDirection='column' gap={1.5}>
            <MDInput
              label='ชื่อเหตุการณ์'
              placeholder='เช่น เตือนนับสต๊อกประจำสัปดาห์'
              fullWidth
              size='small'
              value={name}
              onChange={(e) => setName(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <MDInput
              label='คำอธิบายสั้น (ถ้ามี)'
              fullWidth
              size='small'
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <MDBox>
              <MDTypography variant='caption' fontWeight='bold' display='block' mb={0.5}>
                ตัวแปร · กดเพื่อแทรก
              </MDTypography>
              <MDBox display='flex' flexWrap='wrap' gap={0.5} mb={1}>
                {Object.entries(VARS).map(([v, label]) => (
                  <Chip
                    key={v}
                    size='small'
                    label={`{${v}}`}
                    title={label}
                    onClick={() => insertVar(v)}
                  />
                ))}
              </MDBox>
              <MDInput
                inputRef={areaRef}
                label='ข้อความที่จะส่ง · ใช้ *ตัวหนา* ได้'
                fullWidth
                multiline
                minRows={6}
                value={text}
                onChange={(e) => change(e.target.value)}
                error={Boolean(error)}
                helperText={error || `${text.length}/2000 ตัวอักษร`}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
            <MDBox sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size='small'
                    checked={schedOn}
                    onChange={(e) => setSchedOn(e.target.checked)}
                  />
                }
                label={
                  <MDTypography variant='caption'>
                    ส่งอัตโนมัติตามเวลา (ไม่เปิด = กดส่งเองเท่านั้น)
                  </MDTypography>
                }
              />
              {schedOn && (
                <MDBox display='flex' gap={1} alignItems='center' flexWrap='wrap' mt={1}>
                  <MDInput
                    label='เวลา'
                    type='time'
                    size='small'
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 130 }}
                  />
                  <MDBox display='flex' gap={0.5}>
                    {DAYS.map(([d, label]) => (
                      <Chip
                        key={d}
                        size='small'
                        label={label}
                        color={days.includes(d) ? 'info' : 'default'}
                        variant={days.includes(d) ? 'filled' : 'outlined'}
                        onClick={() => toggleDay(d)}
                        aria-pressed={days.includes(d)}
                      />
                    ))}
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
          <MDBox flex='1 1 280px' minWidth={0}>
            <MDTypography variant='caption' fontWeight='bold' display='block' mb={0.5}>
              ตัวอย่างข้อความที่จะส่ง
            </MDTypography>
            <MDBox
              sx={{
                bgcolor: '#E7F3FF',
                borderRadius: 2,
                p: 1.5,
                minHeight: 160,
                whiteSpace: 'pre-wrap',
                fontSize: 14,
                lineHeight: 1.5,
                border: '1px solid #C9DFF5',
              }}
            >
              {preview || (text.trim() ? '…' : 'พิมพ์ข้อความด้านซ้าย')}
            </MDBox>
            <MDTypography variant='caption' color='text' display='block' mt={1}>
              หลังบันทึก เหตุการณ์จะขึ้นในตาราง ติ๊กกลุ่มที่จะรับได้เหมือนเหตุการณ์ของระบบ
            </MDTypography>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant='outlined' color='secondary' disabled={busy} onClick={onClose}>
          ยกเลิก
        </MDButton>
        <MDButton variant='gradient' color='dark' disabled={busy || !canSave} onClick={save}>
          {busy ? 'กำลังบันทึก…' : 'บันทึก'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

NotifyEventDialog.defaultProps = { event: null, initialMessage: '' };
NotifyEventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    schedule_time: PropTypes.string,
    schedule_days: PropTypes.string,
  }),
  initialMessage: PropTypes.string,
  token: PropTypes.string.isRequired,
  mod: PropTypes.string.isRequired,
  onSaved: PropTypes.func.isRequired,
  onNotify: PropTypes.func.isRequired,
};
